import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar, ChevronLeft, ChevronRight, List, CalendarDays } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { SessionList } from '@/components/coach/session-list';
import { useTodaySessions, useSessionsForMonth } from '@/hooks/use-coach-data';
import { SessionCalendar } from '@/components/shared/session-calendar';

// Format date to YYYY-MM-DD in local timezone
function formatDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatDateDisplay(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.getTime() === today.getTime()) return 'Today';
  if (date.getTime() === tomorrow.getTime()) return 'Tomorrow';
  if (date.getTime() === yesterday.getTime()) return 'Yesterday';

  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

export default function SessionsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedDate, setSelectedDate] = useState(() => formatDateKey(new Date()));
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');

  // Set view mode from URL query param
  useEffect(() => {
    const view = searchParams.get('view');
    if (view === 'calendar') setViewMode('calendar');
  }, [searchParams]);

  const { data, isLoading } = useTodaySessions(selectedDate);
  const { data: monthData } = useSessionsForMonth(
    calendarMonth.getFullYear(),
    calendarMonth.getMonth()
  );

  const navigateDay = (direction: 'prev' | 'next') => {
    const [year, month, day] = selectedDate.split('-').map(Number);
    const current = new Date(year, month - 1, day);
    current.setDate(current.getDate() + (direction === 'next' ? 1 : -1));
    setSelectedDate(formatDateKey(current));
  };

  const handleSessionClick = (sessionId: string) => {
    navigate(`/coach/sessions/${sessionId}/attendance`);
  };

  const handleCalendarMonthChange = (year: number, month: number) => {
    setCalendarMonth(new Date(year, month, 1));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-foreground">Sessions</h1>
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('list')}
          >
            <List className="w-4 h-4 mr-1" />
            List
          </Button>
          <Button
            variant={viewMode === 'calendar' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('calendar')}
          >
            <CalendarDays className="w-4 h-4 mr-1" />
            Calendar
          </Button>
        </div>
      </div>

      {viewMode === 'list' ? (
        <>
          {/* Date Navigation */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Button variant="ghost" size="icon" onClick={() => navigateDay('prev')}>
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-primary" />
                  <span className="text-lg font-semibold text-foreground">{formatDateDisplay(selectedDate)}</span>
                </div>
                <Button variant="ghost" size="icon" onClick={() => navigateDay('next')}>
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Session List */}
          {isLoading ? (
            <Card>
              <CardHeader>
                <CardTitle>Sessions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
              </CardContent>
            </Card>
          ) : data?.sessions.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No sessions scheduled for this day</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">
                {data?.total} Session{data?.total !== 1 ? 's' : ''}
              </h2>
              <SessionList
                sessions={data?.sessions ?? []}
                onSessionClick={(session) => handleSessionClick(session.id)}
              />
            </div>
          )}
        </>
      ) : (
        <SessionCalendar
          sessions={monthData?.sessions ?? []}
          isLoading={!monthData}
          onSessionClick={(session) => handleSessionClick(session.id)}
          onMonthChange={handleCalendarMonthChange}
          variant="coach"
        />
      )}
    </div>
  );
}
